import React, { Component } from "react";
import { Alert, Button, Col, DatePicker, Descriptions, Form, Input, Row, Space, Tag, Typography } from "antd";
import { FullHeightContainerLayout, CopyInput } from "components";
import { API_KEY_STATUS_COLORS, formatApiKeyDateTime, getApiKeyStatus, resolveUserEmail } from "common/apiKeys";
import dayjs from "dayjs";
import _ from "lodash";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { Text } = Typography;

// Mirrors the backend rule: 3-50 characters, starting with a letter or digit; letters, digits, space, hyphen, underscore.
const NAME_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9 _-]*$/;

const toFormValues = (apiKey) => ({
    name: apiKey.name,
    description: apiKey.description || "",
    expiresAt: apiKey.expiresAt ? dayjs(apiKey.expiresAt) : null,
    apiKeyId: apiKey.apiKeyId,
    maskedKey: apiKey.maskedKey,
});

// A chosen expiry day is sent as the end of that day in the user's timezone.
const toExpiresAt = (value) => (value ? dayjs(value).endOf("day").toISOString() : null);

class ManageApiKey extends Component {
    constructor(props) {
        super(props);
        this.state = {
            apiKeyDefault: {
                name: "",
                description: "",
                expiresAt: null,
            },
            readOnlyView: this.props.action === "view",
        };
        this.formRef = React.createRef();
        this.emptyObject = {};
    }

    componentDidMount() {
        if (this.props.apiKey) {
            this.formRef.current.setFieldsValue(toFormValues(this.props.apiKey));
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.apiKey && prevProps.apiKey !== this.props.apiKey) {
            this.formRef.current.setFieldsValue(toFormValues(this.props.apiKey));
        }
    }

    getFormErrors = (fieldName) => {
        if (this.props.formErrors && this.props.formErrors[fieldName]) {
            return {
                help: this.props.formErrors[fieldName],
                validateStatus: "error",
            };
        }
        return this.emptyObject;
    };

    onFormSubmit = (formData) => {
        let name = (formData.name || "").trim();
        let description = (formData.description || "").trim();
        if (this.props.action === "create") {
            this.props.onSave({
                name,
                description,
                expiresAt: toExpiresAt(formData.expiresAt),
                active: true,
            });
            return;
        }

        let original = this.props.apiKey;
        let updatedObject = {};
        if (name !== original.name) {
            updatedObject.name = name;
        }
        if (description !== (original.description || "")) {
            updatedObject.description = description;
        }
        let originalExpiresAt = original.expiresAt || null;
        let expiresAt = originalExpiresAt;
        if (!formData.expiresAt) {
            expiresAt = null;
        } else if (!originalExpiresAt || !dayjs(formData.expiresAt).isSame(dayjs(originalExpiresAt), "day")) {
            expiresAt = toExpiresAt(formData.expiresAt);
        }
        if (expiresAt !== originalExpiresAt) {
            updatedObject.expiresAt = expiresAt;
        }
        if (_.isEmpty(updatedObject) === false) {
            updatedObject.version = original.version;
        }
        this.props.onSave(original.apiKeyId, updatedObject);
    };

    isReadOnlyView = () => {
        return this.state.readOnlyView || !(this.props.permission.canEdit || this.props.permission.canAdd);
    };

    isSaveAllowed = () => {
        if (this.props.action === "edit") {
            return this.props.permission.canEdit;
        } else if (this.props.action === "create") {
            return this.props.permission.canAdd;
        }
        return false;
    };

    disabledExpiryDate = (current) => current && current.isBefore(dayjs().endOf("day"));

    renderKeyDetails = () => {
        let apiKey = this.props.apiKey;
        if (this.props.action === "create" || !apiKey) {
            return <></>;
        }
        let status = getApiKeyStatus(apiKey);
        return (
            <>
                <Form.Item className="wrap-label" label="Key ID" name={["apiKeyId"]} data-testid={CypressTestIds.API_KEY_MANAGE_KEY_ID_INPUT}>
                    <CopyInput readOnly={true} copyToolTip="Copy Key ID" data-testid={CypressTestIds.API_KEY_MANAGE_KEY_ID_COPY_INPUT}></CopyInput>
                </Form.Item>
                <Form.Item
                    className="wrap-label"
                    label="Secret Key"
                    name={["maskedKey"]}
                    extra="The full key is shown only once, when it is created. If it has been lost or exposed, delete this key and create a new one."
                >
                    <Input readOnly={true} className="font-mono" data-testid={CypressTestIds.API_KEY_MANAGE_SECRET_INPUT} />
                </Form.Item>
                <Descriptions
                    size="small"
                    column={3}
                    className="mb-4"
                    items={[
                        { key: "status", label: "Status", children: <Tag color={API_KEY_STATUS_COLORS[status]}>{status}</Tag> },
                        { key: "created", label: "Created", children: <Text>{formatApiKeyDateTime(apiKey.createdDate, "")}</Text> },
                        { key: "createdBy", label: "Created by", children: <Text>{resolveUserEmail(this.props.userList, apiKey.createdBy)}</Text> },
                        { key: "lastUsed", label: "Last used", children: <Text>{formatApiKeyDateTime(apiKey.lastUsedDate)}</Text> },
                        { key: "updated", label: "Updated", children: <Text>{formatApiKeyDateTime(apiKey.updatedDate, "")}</Text> },
                        { key: "updatedBy", label: "Updated by", children: <Text>{resolveUserEmail(this.props.userList, apiKey.updatedBy)}</Text> },
                    ]}
                />
            </>
        );
    };

    render() {
        let readOnly = this.isReadOnlyView();
        return (
            <Form
                layout="horizontal"
                labelAlign="left"
                ref={this.formRef}
                name="basic"
                onFinish={this.onFormSubmit}
                initialValues={this.state.apiKeyDefault}
                onValuesChange={this.props.onValuesChanged}
                style={{ height: "100%" }}
            >
                <FullHeightContainerLayout
                    showFooter={true}
                    content={
                        <>
                            {this.props.action === "create" ? (
                                <Alert
                                    className="mb-4"
                                    type="info"
                                    showIcon
                                    title="The API key is shown only once, right after it is created. Store it in a secret manager before closing that dialog."
                                />
                            ) : (
                                <></>
                            )}
                            <Form.Item
                                className="wrap-label"
                                {...this.getFormErrors("name")}
                                label="Name"
                                name={["name"]}
                                rules={[
                                    { required: true, message: `Name is required` },
                                    { min: 3, message: `Name should be at least 3 characters long` },
                                    { max: 50, message: `Name should be at most 50 characters long` },
                                    { pattern: NAME_PATTERN, message: `Name should start with a letter or number and contain only letters, numbers, spaces, hyphens or underscores` },
                                ]}
                                hasFeedback
                            >
                                <Input placeholder="e.g. CI pipeline" readOnly={readOnly} maxLength={50} data-testid={CypressTestIds.API_KEY_MANAGE_NAME_INPUT} />
                            </Form.Item>
                            <Form.Item
                                className="wrap-label"
                                {...this.getFormErrors("description")}
                                label="Description"
                                name={["description"]}
                                rules={[{ max: 250, message: `Description should be at most 250 characters long` }]}
                            >
                                <Input.TextArea
                                    placeholder="What this key is used for"
                                    readOnly={readOnly}
                                    rows={2}
                                    maxLength={250}
                                    data-testid={CypressTestIds.API_KEY_MANAGE_DESCRIPTION_INPUT}
                                />
                            </Form.Item>
                            <Form.Item
                                className="wrap-label"
                                {...this.getFormErrors("expiresAt")}
                                label="Expires"
                                name={["expiresAt"]}
                                extra={readOnly ? undefined : "Leave empty for a key that never expires."}
                            >
                                <DatePicker
                                    placeholder="Never"
                                    format="MMM D, YYYY"
                                    style={{ width: "100%" }}
                                    disabledDate={this.disabledExpiryDate}
                                    inputReadOnly={readOnly}
                                    allowClear={!readOnly}
                                    {...(readOnly ? { open: false } : {})}
                                    data-testid={CypressTestIds.API_KEY_MANAGE_EXPIRES_AT_INPUT}
                                />
                            </Form.Item>
                            {this.renderKeyDetails()}
                        </>
                    }
                    footer={
                        <Row>
                            <Col span={24} className="footer-right-column">
                                <Space>
                                    <Button onClick={this.props.onCancel} data-testid={CypressTestIds.API_KEY_MANAGE_CANCEL_BUTTON}>
                                        Cancel
                                    </Button>
                                    <Button type="primary" htmlType="submit" disabled={readOnly || !this.isSaveAllowed()} data-testid={CypressTestIds.API_KEY_MANAGE_SAVE_BUTTON}>
                                        Save
                                    </Button>
                                </Space>
                            </Col>
                        </Row>
                    }
                ></FullHeightContainerLayout>
            </Form>
        );
    }
}

export default ManageApiKey;
